import React, { useState } from 'react';
import { Phone, Mail, MapPin, Calendar, Send, Clock, CheckCircle } from 'lucide-react';
import { companyInfo } from '../mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Erreur serveur');
      
      setSent(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="section section-alt">
      <div className="container">
        <div className="text-center mb-6">
          <h2 className="heading-1 mb-3">Contactez-moi</h2>
          <p className="body-large text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
            Une question ? Un projet ? Parlons-en ensemble lors d'un premier échange gratuit
          </p>
        </div>

        <div className="grid-2" style={{ gap: '4rem', alignItems: 'flex-start' }}>
          {/* Informations de contact */}
          <div>
            {/* Bouton Calendly - outline brown */}
            <div className="card" style={{ 
              background: 'var(--bg-card)',
              border: '2px solid var(--primary-brown)',
              textAlign: 'center',
              marginBottom: '2rem',
              boxShadow: '0 6px 20px rgba(58, 46, 15, 0.08)'
            }}>
              <h3 className="heading-3 mb-2" style={{ color: 'var(--primary-brown)' }}>Prise de rendez-vous rapide</h3>
              <p className="body-medium mb-4">
                Réservez directement un créneau dans mon agenda pour un premier échange gratuit
              </p>
              <a 
                href={companyInfo.calendlyUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ width: '100%' }}
              >
                <Calendar size={16} />
                Prendre rendez-vous
              </a>
            </div>

            <div className="card" style={{ 
              background: 'var(--bg-card)',
              border: '2px solid var(--primary-brown)',
              boxShadow: '0 6px 20px rgba(58, 46, 15, 0.08)'
            }}>
              <h3 className="heading-3 mb-4" style={{ color: 'var(--primary-brown)' }}>Informations pratiques</h3>
              
              <div className="mb-4">
                <div className="flex" style={{ alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-gold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px'
                  }}>
                    <Phone size={20} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="body-medium" style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      Téléphone
                    </p>
                    <a href={`tel:${companyInfo.phone}`} className="body-small text-muted" style={{ wordBreak: 'break-word' }}>
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex" style={{ alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-gold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px'
                  }}>
                    <Mail size={20} color="white" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="body-medium" style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      Email
                    </p>
                    <a href={`mailto:${companyInfo.email}`} className="body-small text-muted" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex" style={{ alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-gold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px'
                  }}>
                    <MapPin size={20} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="body-medium" style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      Zone d'intervention
                    </p>
                    <p className="body-small text-muted">{companyInfo.address}</p>
                    <p className="body-small text-muted">France entière</p>
                  </div>
                </div>

                <div className="flex" style={{ alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-gold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px'
                  }}>
                    <Clock size={20} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p className="body-medium" style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      Horaires
                    </p>
                    <p className="body-small text-muted">Lun - Ven : 9h00 - 18h00</p>
                    <p className="body-small text-muted">Sur rendez-vous</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire de contact - DARK BROWN HIGH CONTRAST CTA */}
          <div className="card" data-testid="contact-form-card" style={{
            background: 'linear-gradient(160deg, var(--primary-brown) 0%, #2a2008 100%)',
            border: '1px solid rgba(172, 153, 105, 0.35)',
            boxShadow: '0 20px 50px rgba(58, 46, 15, 0.25)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative gold corner */}
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '140px',
              height: '140px',
              background: 'radial-gradient(circle, rgba(172, 153, 105, 0.22) 0%, transparent 70%)',
              borderRadius: '50%',
              pointerEvents: 'none'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Badge - matches hero offer badge style */}
              <div data-testid="contact-offer-badge" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.55rem',
                padding: '0.55rem 1.1rem',
                border: '1.5px solid var(--primary-gold)',
                borderRadius: '2rem',
                background: 'rgba(172, 153, 105, 0.16)',
                marginBottom: '1.2rem',
                boxShadow: '0 0 24px rgba(172, 153, 105, 0.22)'
              }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--primary-gold)',
                  boxShadow: '0 0 10px var(--primary-gold)'
                }} />
                <span style={{
                  color: 'var(--primary-gold)',
                  fontWeight: '700',
                  fontSize: '0.78rem',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase'
                }}>
                  Échange gratuit
                </span>
              </div>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.6rem',
                fontWeight: '600',
                color: 'white',
                marginBottom: '0.5rem',
                letterSpacing: '-0.01em'
              }}>Envoyez-moi un message</h3>
              <p style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: '0.92rem',
                marginBottom: '1.75rem',
                lineHeight: 1.6
              }}>
                Décrivez votre situation ou vos objectifs — je vous réponds personnellement sous 24 h.
              </p>

            <form onSubmit={handleSubmit}>
              <div className="grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ color: 'var(--primary-gold)' }}>Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input contact-dark-input"
                    required
                    placeholder="Votre nom"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ color: 'var(--primary-gold)' }}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input contact-dark-input"
                    required
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div className="grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ color: 'var(--primary-gold)' }}>Téléphone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input contact-dark-input"
                    placeholder="06 00 00 00 00"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ color: 'var(--primary-gold)' }}>Sujet *</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input form-select contact-dark-input"
                    required
                  >
                    <option value="">Choisissez un sujet</option>
                    <option value="Optimiser son épargne">Optimiser son épargne</option>
                    <option value="Préparer sa retraite">Préparer sa retraite</option>
                    <option value="Études des enfants">Études des enfants</option>
                    <option value="Immobilier">Immobilier</option>
                    <option value="Préparer sa transmission">Préparer sa transmission</option>
                    <option value="Audit patrimonial">Demande d'audit patrimonial</option>
                    <option value="Solutions entreprise">Solutions entreprise</option>
                    <option value="Formation/Reconversion">Formation/Reconversion</option>
                    <option value="Autre demande">Autre demande</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label" style={{ color: 'var(--primary-gold)' }}>Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-input contact-dark-input"
                  required
                  rows="5"
                  placeholder="Décrivez votre projet ou votre situation..."
                  style={{ minHeight: '120px', resize: 'vertical' }}
                />
              </div>

              <button 
                type="submit" 
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  background: 'linear-gradient(135deg, var(--primary-gold) 0%, #c9a961 50%, #d4af37 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '2.5rem',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: '700',
                  fontSize: '1rem',
                  letterSpacing: '0.02em',
                  cursor: sending ? 'wait' : 'pointer',
                  boxShadow: '0 10px 28px rgba(172, 153, 105, 0.45)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.6rem',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                disabled={sending}
                data-testid="contact-submit-btn"
                onMouseEnter={(e) => {
                  if (!sending) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 14px 36px rgba(172, 153, 105, 0.6)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 28px rgba(172, 153, 105, 0.45)';
                }}
              >
                {sending ? 'Envoi en cours...' : sent ? (
                  <><CheckCircle size={18} /> Message envoyé !</>
                ) : (
                  <><Send size={18} /> Envoyer le message</>
                )}
              </button>
            </form>

            {sent && (
              <div data-testid="contact-success-msg" style={{
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginTop: '1rem',
                textAlign: 'center'
              }}>
                <p className="body-small" style={{ color: '#6ee7b7', fontWeight: '500' }}>
                  Votre message a bien été envoyé. Je vous répondrai dans les plus brefs délais.
                </p>
              </div>
            )}

            {error && (
              <div data-testid="contact-error-msg" style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginTop: '1rem',
                textAlign: 'center'
              }}>
                <p className="body-small" style={{ color: '#fca5a5' }}>{error}</p>
              </div>
            )}

            <p style={{
              textAlign: 'center',
              marginTop: '1rem',
              fontSize: '0.78rem',
              color: 'rgba(255,255,255,0.5)',
              fontStyle: 'italic'
            }}>
              * Champs obligatoires — Réponse personnelle sous 24 h.
            </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;